<template>
  <custom-modal
    v-model:visible="visibleProxy"
    :header-title="mode === 'first' ? '设置登录密码' : mode === 'reset' ? '重置密码' : '修改密码'"
    :confirm-loading="loading"
    width="500px"
    :show-reset-button="false"
    confirm-button-text="确定"
    @confirm="handleSubmit"
    @cancel="handleCancel"
  >
    <a-form
      ref="formRef"
      :model="form"
      :rules="rules"
      layout="vertical"
    >
      <!-- 旧密码修改模式 -->
      <template v-if="mode === 'old'">
        <a-form-item label="旧密码" name="oldpass">
          <a-input-password
            v-model:value="form.oldpass"
            placeholder="请输入旧密码"
            size="large"
          />
        </a-form-item>
      </template>

      <!-- 手机验证码重置模式 -->
      <template v-else-if="mode === 'reset'">
        <a-form-item label="手机号" name="mobile">
          <a-input
            v-model:value="form.mobile"
            :disabled="mobileDisabled"
            placeholder="请输入手机号"
            size="large"
          />
        </a-form-item>
        <a-form-item label="验证码" name="code">
          <div class="flex gap-2">
            <a-input
              v-model:value="form.code"
              :maxlength="6"
              placeholder="请输入验证码"
              size="large"
              class="flex-1"
            />
            <a-button
              type="primary"
              size="large"
              :loading="smsSending"
              :disabled="smsCountdown > 0"
              @click="sendResetSms"
              class="w-[120px]"
            >
              {{ smsCountdown > 0 ? `${smsCountdown}秒后重试` : '获取验证码' }}
            </a-button>
          </div>
        </a-form-item>
      </template>

      <a-form-item label="新密码" name="loginpass">
        <a-input-password
          v-model:value="form.loginpass"
          placeholder="请输入新密码"
          size="large"
        />
      </a-form-item>
      <a-form-item label="确认密码" name="quepass">
        <a-input-password
          v-model:value="form.quepass"
          placeholder="请再次输入新密码"
          size="large"
        />
      </a-form-item>
    </a-form>

    <!-- 图形验证码弹窗 -->
    <captcha-dialog
      v-model:visible="captchaDialogVisible"
      @confirm="handleCaptchaConfirm"
      @close="handleCaptchaDialogClose"
    />
  </custom-modal>
</template>

<script>
import { message } from 'ant-design-vue'
import CustomModal from '@/components/Modal'
import CaptchaDialog from '@/components/CaptchaDialog'
import { setLoginPass, forgetPass, setCodePass } from '@/api/englishLearning/public'
import { sendSms } from '@/api/modules/login'
import { isValidPhone, phoneHasWhitespace, validatePhoneForSms, PHONE_NO_SPACE_MSG } from '@/utils/phoneValidate.js'

export default {
  name: 'ResetPassword',
  components: { CustomModal, CaptchaDialog },
  props: {
    visible: { type: Boolean, default: false }
  },
  data () {
    return {
      visibleProxy: false,
      loading: false,
      mode: 'old', // old: 旧密码修改, reset: 短信验证码重置, first: 首次设置密码
      fromLogin: false,
      mobileDisabled: false,
      smsSending: false,
      smsCountdown: 0,
      smsTimer: null,
      captchaDialogVisible: false,
      pendingSmsAction: null,
      form: {
        oldpass: '',
        mobile: '',
        code: '',
        loginpass: '',
        quepass: ''
      },
      rules: {
        mobile: [
          {
            required: true,
            validator: (rule, value) => {
              if (this.mode !== 'reset') return Promise.resolve()
              if (!value) return Promise.reject('请输入手机号')
              if (phoneHasWhitespace(value)) return Promise.reject(PHONE_NO_SPACE_MSG)
              if (!isValidPhone(value)) return Promise.reject('请输入正确的手机号')
              return Promise.resolve()
            },
            trigger: 'blur'
          }
        ],
        code: [
          {
            required: true,
            validator: (rule, value) => {
              if (this.mode !== 'reset') return Promise.resolve()
              if (!value) return Promise.reject('请输入验证码')
              return Promise.resolve()
            },
            trigger: 'blur'
          }
        ],
        oldpass: [
          {
            required: true,
            validator: (rule, value) => {
              if (this.mode !== 'old') return Promise.resolve()
              if (!value) return Promise.reject('请输入旧密码')
              return Promise.resolve()
            },
            trigger: 'blur'
          }
        ],
        loginpass: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          {
            validator: (rule, value) => {
              if (!value) return Promise.reject('请输入新密码')
              if (!/^(?![0-9]+$)(?![a-zA-Z]+$)[0-9A-Za-z]{6,12}$/.test(value)) {
                return Promise.reject('请输入6-12位包含字母和数字的密码')
              }
              return Promise.resolve()
            },
            trigger: 'blur'
          }
        ],
        quepass: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          {
            validator: (rule, value) => {
              if (!value) return Promise.reject('请再次输入新密码')
              if (value !== this.form.loginpass) return Promise.reject('两次密码不一致')
              return Promise.resolve()
            },
            trigger: 'blur'
          }
        ]
      }
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (val) {
        this.visibleProxy = val
      }
    },
    visibleProxy (val) {
      this.$emit('update:visible', val)
    }
  },
  beforeUnmount () {
    if (this.smsTimer) {
      clearInterval(this.smsTimer)
      this.smsTimer = null
    }
  },
  methods: {
    init (options = {}) {
      this.loading = false
      this.mode = options.mode || 'old'
      this.fromLogin = !!options.fromLogin
      this.form = {
        oldpass: '',
        mobile: options.mobile || '',
        code: '',
        loginpass: '',
        quepass: ''
      }
      this.mobileDisabled = !!options.mobile
      this.visibleProxy = true
    },
    handleCancel () {
      this.visibleProxy = false
    },
    async sendResetSms () {
      if (this.smsSending || this.smsCountdown > 0) return
      const pv = validatePhoneForSms(this.form.mobile)
      if (!pv.ok) {
        message.error(pv.message)
        return
      }

      this.pendingSmsAction = { mobile: pv.mobile, type: 'forget' }
      this.captchaDialogVisible = true
    },
    async handleCaptchaConfirm ({ captcha, uuid }) {
      if (!this.pendingSmsAction) {
        message.error('操作异常，请重试')
        this.captchaDialogVisible = false
        return
      }

      try {
        this.smsSending = true
        const res = await sendSms({
          mobile: this.pendingSmsAction.mobile,
          type: this.pendingSmsAction.type,
          captcha,
          uuid
        })

        this.captchaDialogVisible = false
        this.pendingSmsAction = null

        message.success(res?.msg || '验证码已发送')
        this.smsSending = false
        this.smsCountdown = 60
        if (this.smsTimer) clearInterval(this.smsTimer)
        this.smsTimer = setInterval(() => {
          if (this.smsCountdown <= 1) {
            clearInterval(this.smsTimer)
            this.smsTimer = null
            this.smsCountdown = 0
          } else {
            this.smsCountdown -= 1
          }
        }, 1000)
      } catch (err) {
        console.error('发送验证码失败', err)
        const errorMsg = err?.msg || err?.message || '发送验证码失败'
        message.error(errorMsg)
        this.smsSending = false

        if (!errorMsg.includes('验证码') && !errorMsg.includes('captcha')) {
          this.captchaDialogVisible = false
        }
      }
    },
    handleCaptchaDialogClose () {
      this.captchaDialogVisible = false
      this.pendingSmsAction = null
    },
    async handleSubmit () {
      try {
        await this.$refs.formRef.validate()
        this.loading = true

        if (this.mode === 'old') {
          const res = await setLoginPass(this.form)
          message.success(res?.msg || '修改成功')
          this.visibleProxy = false
          this.$store.dispatch('user/logout').then(() => {
            this.$router.push({ path: '/login' })
          })
        } else if (this.mode === 'reset') {
          const pv = validatePhoneForSms(this.form.mobile)
          if (!pv.ok) {
            message.error(pv.message)
            return
          }
          const res = await forgetPass({
            mobile: pv.mobile,
            code: this.form.code,
            loginpass: this.form.loginpass,
            quepass: this.form.quepass
          })
          message.success(res?.msg || '重置成功')
          this.visibleProxy = false
          if (this.fromLogin) {
            this.$emit('success')
          } else {
            this.$store.dispatch('user/logout').then(() => {
              this.$router.push({ path: '/login' })
            })
          }
        } else {
          const res = await setCodePass({
            loginpass: this.form.loginpass,
            quepass: this.form.quepass
          })
          message.success(res?.msg || '设置成功')
          this.visibleProxy = false
          if (this.fromLogin) {
            this.$emit('success')
          } else {
            this.$store.dispatch('user/logout').then(() => {
              this.$router.push({ path: '/login' })
            })
          }
        }
      } catch (error) {
        console.error('提交失败:', error)
        if (!error.errorFields) {
          message.error(error?.msg || error?.message || '操作失败')
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
